/**
 * 헤더 컴포넌트
 * - 앱 제목 및 네비게이션
 * - 로그인 상태 표시
 * - 모바일: 로고 + LangSwitcher만 표시 (네비게이션은 BottomNav로 대체)
 */
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/Button';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import LangSwitcher from '../ui/LangSwitcher';
import UserMenu from './UserMenu';
import { NotificationBell } from './NotificationBell';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { online, pendingCount, handleSync } = useOfflineSync();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  /** 현재 경로와 일치하면 active 스타일 반환 */
  const navLinkClass = (path: string) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    return isActive
      ? 'bg-white/20 text-white font-semibold border-b-2 border-white rounded-lg px-3 py-2'
      : 'bg-transparent text-white/75 hover:bg-white/20 hover:text-white transition-colors rounded-lg px-3 py-2';
  };

  return (
    <header className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* 로고 및 제목 */}
          <Link to="/" aria-label="홈으로 이동" className="flex items-center space-x-3 hover:opacity-90 transition-opacity">
            <div className="w-8 h-8 xs:w-10 xs:h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-2xl">💿</span>
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">MD Label Maker</h1>
              <p className="hidden xl:block text-xs text-blue-100">미니디스크 라벨 제작기</p>
            </div>
          </Link>

          {/* 데스크톱/태블릿 네비게이션 */}
          <nav aria-label={t('header.mainNav')} className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <Link to="/gallery" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
              <Button variant="default" size="sm" className={`${navLinkClass('/gallery')} min-h-[44px]`}>
                {t('header.gallery')}
              </Button>
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/albums" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
                  <Button variant="default" size="sm" className={`${navLinkClass('/albums')} min-h-[44px]`}>
                    {t('header.albums')}
                  </Button>
                </Link>
                <Link to="/pricing" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
                  <Button variant="default" size="sm" className={`${navLinkClass('/pricing')} min-h-[44px]`}>
                    {t('header.pricing')}
                  </Button>
                </Link>
              </>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-2 ml-2 lg:ml-4 border-l border-blue-400 pl-2 lg:pl-4">
                <NotificationBell />
                <UserMenu user={user} onLogout={handleLogout} />
              </div>
            ) : (
              <Link to="/login">
                <Button variant="default" size="sm" className={`${navLinkClass('/login')} min-h-[44px]`}>
                  {t('common.login')}
                </Button>
              </Link>
            )}

            {/* 오프라인/동기화 대기 배지 */}
            {!online && (
              <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {t('header.offline')}
              </span>
            )}
            {pendingCount > 0 && online && (
              <button
                onClick={handleSync}
                className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                title="동기화 실행"
              >
                {t('header.syncPending', { count: pendingCount })}
              </button>
            )}

            {/* 언어 선택 */}
            <LangSwitcher />
          </nav>

          {/* 모바일 우측: 언어 선택만 표시 (네비는 BottomNav로) */}
          <div className="flex md:hidden items-center space-x-2">
            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-sm text-white/75 hover:text-white px-3 py-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                {t('common.login')}
              </Link>
            )}
            <LangSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
